import { useAuth } from '@/auth/useAuth';
import { useOrganizationPerms } from '@/organization/useOrganizationRole';
import { useOrganizationDepartments } from '@/organization/useOrganizations';
import { updateOrganizationUser } from '@/services/userService';
import type { Department } from '@/types/department';
import type { Member } from '@/types/user';
import {
  Autocomplete,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

const roles = [
  'Organization Admin',
  'Program Manager',
  'Budget Manager',
  'General User',
];

interface Props {
  open: boolean;
  user: Member | null;
  onClose: () => void;
  organizationId: string;
}

export function EditUserModal({ open, user, onClose, organizationId }: Props) {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: '',
    selectedDepartments: [] as Department[],
  });

  const { data: departments } = useOrganizationDepartments(organizationId);
  const { user: _currentUser } = useAuth();
  const { data: perms } = useOrganizationPerms(organizationId);
  const canManageUsers = perms?.canManageUsers;
  const queryClient = useQueryClient();

  useEffect(() => {
    if (user) {
      setForm({
        firstName: user.userDetails.firstName,
        lastName: user.userDetails.lastName,
        email: user.userDetails.email,
        role: user.role,
        selectedDepartments:
          departments?.filter((d) =>
            user.userDetails.departments?.some((ud) => ud.id === d.id),
          ) || [],
      });
    }
  }, [user, departments]);

  const mutation = useMutation({
    mutationFn: () => {
      if (!user?.userDetails.uuid) {
        throw new Error('User UUID is missing');
      }
      return updateOrganizationUser(organizationId, user.userDetails.uuid, {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        role: form.role,
        departmentIds: form.selectedDepartments.map((d) => d.id),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['organization-users', organizationId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['organization-current-user', organizationId],
      });
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Edit User</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label='First Name'
            value={form.firstName}
            onChange={(e) =>
              setForm((f) => ({ ...f, firstName: e.target.value }))
            }
            fullWidth
          />
          <TextField
            label='Last Name'
            value={form.lastName}
            onChange={(e) =>
              setForm((f) => ({ ...f, lastName: e.target.value }))
            }
            fullWidth
          />
          <TextField
            label='Email'
            type='email'
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            fullWidth
          />
          {canManageUsers && (
            <Select
              value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
              fullWidth
            >
              {roles.map((r) => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </Select>
          )}
          <Autocomplete
            multiple
            size='small'
            options={departments || []}
            getOptionLabel={(option) => option.name}
            value={form.selectedDepartments}
            onChange={(_event, newValue) => {
              setForm((f) => ({
                ...f,
                selectedDepartments: newValue,
              }));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label='Departments'
                placeholder='Select departments'
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip
                    key={key}
                    size='small'
                    label={option.name}
                    {...tagProps}
                  />
                );
              })
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant='contained'
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending || !user}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
